import { useEffect } from 'react';
import useEditorStore from '../stores/useEditorStore';
import useWorkflowStore from '../stores/useWorkflowStore';
import { sceneHasLoadedMesh } from '../lib/scanMeshLoadGate';
import {
  captureStepCheckpoint,
  isCheckpointStep,
  useStepCheckpointStore,
} from '../../features/step-checkpoints';

/** Debounce window after the last change before writing the scene cache. */
const AUTOSAVE_DELAY_MS = 1200;

/**
 * Keep the *current* step's scene mirrored into the client cache so a reload can
 * restore it. The in-session checkpoint system only captures a step when the
 * user leaves it, which means the step being worked on — the one a reload lands
 * on — is otherwise never cached.
 *
 * Re-runs on every geometry mutation (and on tool-setting changes via
 * `toolSettingsRevision`), debounced so rapid edits collapse into one write.
 */
export function useBkSceneAutosave() {
  const geometryVersion = useEditorStore((s) => s.geometryVersion);
  const socketTransformRevision = useEditorStore((s) => s.socketTransformRevision);
  const currentStep = useWorkflowStore((s) => s.currentStep);
  // A restore/revert rewrites the scene under us. Snapshotting mid-flight would
  // save a half-installed model, and on a backward revert it would re-persist the
  // very step whose work was just discarded. Kept as a dependency so the autosave
  // resumes (and captures the settled scene) once the restore finishes.
  const isRestoring = useStepCheckpointStore((s) => s.isRestoring);

  useEffect(() => {
    if (isRestoring) return;
    if (!currentStep || !isCheckpointStep(currentStep)) return;
    if (!sceneHasLoadedMesh(useEditorStore.getState().sceneRefs)) return;
    if (useEditorStore.getState().isLoading) return;

    const id = setTimeout(() => {
      // captureStepCheckpoint snapshots mesh + session and mirrors to the cache.
      captureStepCheckpoint(currentStep);
    }, AUTOSAVE_DELAY_MS);

    return () => clearTimeout(id);
  }, [geometryVersion, socketTransformRevision, currentStep, isRestoring]);
}
